import React, { type ReactElement } from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom'

import './index.css'
import { AppShell } from '@/components/AppShell'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { MobileShell } from '@/components/MobileShell'
import { Toaster } from '@/components/ui/Toaster'
import { ROUTE_ACCESS } from '@/features/auth/access'
import { RequirePermission } from '@/features/auth/RequirePermission'
import { RequireAuth } from '@/features/auth/RequireAuth'
import { useAuth } from '@/features/auth/store'
import { queryClient } from '@/lib/queryClient'
import { ChartOfAccounts } from '@/routes/ChartOfAccounts'
import { CompanySettings } from '@/routes/CompanySettings'
import { Dashboard } from '@/routes/Dashboard'
import { ForgotPassword } from '@/routes/ForgotPassword'
import { GeneralLedger } from '@/routes/GeneralLedger'
import { InvoiceDetail } from '@/routes/InvoiceDetail'
import { Invoices } from '@/routes/Invoices'
import { JournalEntries } from '@/routes/JournalEntries'
import { JournalEntryDetail } from '@/routes/JournalEntryDetail'
import { JournalEntryNew } from '@/routes/JournalEntryNew'
import { Login } from '@/routes/Login'
import { CommissionRules } from '@/routes/CommissionRules'
import { CommissionStatement } from '@/routes/CommissionStatement'
import { CustomerDetail } from '@/routes/CustomerDetail'
import { Customers } from '@/routes/Customers'
import { FieldRoutes } from '@/routes/FieldRoutes'
import { NearbyLeads } from '@/routes/NearbyLeads'
import { EditProfile } from '@/routes/EditProfile'
import { Employees } from '@/routes/Employees'
import { MobileCheckIn } from '@/routes/mobile/MobileCheckIn'
import { MobileHome } from '@/routes/mobile/MobileHome'
import { MobileLeadCapture } from '@/routes/mobile/MobileLeadCapture'
import { MobileRoute } from '@/routes/mobile/MobileRoute'
import { MobileVisit } from '@/routes/mobile/MobileVisit'
import { OrderDetail } from '@/routes/OrderDetail'
import { OrderNew } from '@/routes/OrderNew'
import { Orders } from '@/routes/Orders'
import { PayrollPreview } from '@/routes/PayrollPreview'
import { PointOfSale } from '@/routes/PointOfSale'
import { PosReceipt } from '@/routes/PosReceipt'
import { PerformanceReviewDetail } from '@/routes/PerformanceReviewDetail'
import { PerformanceReviews } from '@/routes/PerformanceReviews'
import { PosShift } from '@/routes/PosShift'
import { PosShifts } from '@/routes/PosShifts'
import { PosTerminals } from '@/routes/PosTerminals'
import { ProductDetail } from '@/routes/ProductDetail'
import { ProductEdit, ProductNew } from '@/routes/ProductEdit'
import { Products } from '@/routes/Products'
import { Categories } from '@/routes/Categories'
import { TaxRules } from '@/routes/TaxRules'
import { ApplicationDetail } from '@/routes/ApplicationDetail'
import { Applications } from '@/routes/Applications'
import { Attendance } from '@/routes/Attendance'
import { Candidates } from '@/routes/Candidates'
import { JobPostings } from '@/routes/JobPostings'
import { ResetPassword } from '@/routes/ResetPassword'
import { Roles } from '@/routes/Roles'
import { Locations } from '@/routes/Locations'
import { StockAdjustments } from '@/routes/StockAdjustments'
import { CustomerAnalytics } from '@/routes/analytics/CustomerAnalytics'
import { InventoryAnalytics } from '@/routes/analytics/InventoryAnalytics'
import { ProductAnalytics } from '@/routes/analytics/ProductAnalytics'
import { SalesAnalytics } from '@/routes/analytics/SalesAnalytics'
import { StockValuation } from '@/routes/StockValuation'
import { SystemStatus } from '@/routes/SystemStatus'
import { TrialBalance } from '@/routes/TrialBalance'
import { Users } from '@/routes/Users'
import { LandingPage } from '@/routes/LandingPage'
import { StorefrontLayout } from '@/routes/storefront/StorefrontLayout'
import { Storefront } from '@/routes/storefront/Storefront'
import { StorefrontCareerDetail } from '@/routes/storefront/StorefrontCareerDetail'
import { StorefrontCareers } from '@/routes/storefront/StorefrontCareers'
import { StorefrontCart } from '@/routes/storefront/StorefrontCart'
import { StorefrontCheckout } from '@/routes/storefront/StorefrontCheckout'
import { StorefrontLogin } from '@/routes/storefront/StorefrontLogin'
import { StorefrontOrderDetail } from '@/routes/storefront/StorefrontOrderDetail'
import { StorefrontOrders } from '@/routes/storefront/StorefrontOrders'
import { StorefrontProduct } from '@/routes/storefront/StorefrontProduct'
import { StorefrontSignup } from '@/routes/storefront/StorefrontSignup'

/**
 * A route wrapped in whatever permission `ROUTE_ACCESS` says its path needs.
 *
 * Looked up by path rather than passed in, so the table stays the one place a screen's
 * requirement is written down and the sidebar cannot offer a link the guard will bounce.
 * A path the table does not mention is open to any signed-in user.
 */
const guarded = (path: string, element: ReactElement) => ({
  path,
  element: <RequirePermission requirement={ROUTE_ACCESS[path]}>{element}</RequirePermission>,
})

/**
 * A data router rather than `<BrowserRouter>`.
 *
 * Nothing here loads data through the router -- react-query still does that -- but
 * `useBlocker` only exists on this one, and blocking navigation is how a half-filled form
 * survives a click on the sidebar. See `useDirtyGuard`.
 */
const router = createBrowserRouter([
  { path: '/', element: <LandingPage /> },
  { path: '/login', element: <Login /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password', element: <ResetPassword /> },
  {
    // The storefront (M24): its own light-themed shell, sitting outside `RequireAuth` the
    // same way `/login` and the others above do.
    //
    // *All* of it sits outside, buying included, and that is deliberate. `RequireAuth`
    // bounces to `/login`, which lands on `/dashboard` -- a screen a shopper holding the
    // `guest` role has no permission to see. The four routes below that do need a session
    // check `useAuth` themselves and redirect to `/store/login`, so a half-finished
    // basket comes back to the shop rather than to the ERP.
    //
    // `/store/cart` and its siblings are listed after `/store/:productId` only for
    // readability: react-router ranks a static segment above a dynamic one regardless of
    // order, so `cart` is never read as a product id.
    element: <StorefrontLayout />,
    children: [
      { path: '/store', element: <Storefront /> },
      { path: '/store/careers', element: <StorefrontCareers /> },
      { path: '/store/careers/:jobPostingId', element: <StorefrontCareerDetail /> },
      { path: '/store/cart', element: <StorefrontCart /> },
      { path: '/store/checkout', element: <StorefrontCheckout /> },
      { path: '/store/login', element: <StorefrontLogin /> },
      { path: '/store/signup', element: <StorefrontSignup /> },
      { path: '/store/orders', element: <StorefrontOrders /> },
      { path: '/store/orders/:orderId', element: <StorefrontOrderDetail /> },
      { path: '/store/:productId', element: <StorefrontProduct /> },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/dashboard', element: <Dashboard /> },
          { path: '/profile', element: <EditProfile /> },
          guarded('/analytics/sales', <SalesAnalytics />),
          guarded('/analytics/products', <ProductAnalytics />),
          guarded('/analytics/customers', <CustomerAnalytics />),
          guarded('/analytics/inventory', <InventoryAnalytics />),
          guarded('/orders', <Orders />),
          guarded('/orders/new', <OrderNew />),
          guarded('/orders/:orderId', <OrderDetail />),
          guarded('/pos', <PointOfSale />),
          guarded('/pos/shift', <PosShift />),
          guarded('/pos/shifts', <PosShifts />),
          guarded('/pos/terminals', <PosTerminals />),
          guarded('/pos/sales/:transactionId', <PosReceipt />),
          guarded('/products', <Products />),
          guarded('/products/new', <ProductNew />),
          guarded('/products/:productId', <ProductDetail />),
          guarded('/products/:productId/edit', <ProductEdit />),
          guarded('/categories', <Categories />),
          guarded('/stock/adjustments', <StockAdjustments />),
          guarded('/stock/valuation', <StockValuation />),
          guarded('/stock/locations', <Locations />),
          guarded('/tax-rules', <TaxRules />),
          guarded('/invoices', <Invoices />),
          guarded('/invoices/:invoiceId', <InvoiceDetail />),
          guarded('/accounting/accounts', <ChartOfAccounts />),
          guarded('/accounting/ledger/:accountId', <GeneralLedger />),
          guarded('/accounting/journal', <JournalEntries />),
          guarded('/accounting/journal/new', <JournalEntryNew />),
          guarded('/accounting/journal/:entryId', <JournalEntryDetail />),
          guarded('/accounting/trial-balance', <TrialBalance />),
          guarded('/commissions/statement', <CommissionStatement />),
          guarded('/commissions/payroll', <PayrollPreview />),
          guarded('/commissions/rules', <CommissionRules />),
          guarded('/customers', <Customers />),
          guarded('/customers/:customerId', <CustomerDetail />),
          guarded('/leads', <NearbyLeads />),
          guarded('/field-routes', <FieldRoutes />),
          guarded('/employees', <Employees />),
          guarded('/hr/reviews', <PerformanceReviews />),
          guarded('/hr/reviews/:reviewId', <PerformanceReviewDetail />),
          guarded('/hr/attendance', <Attendance />),
          guarded('/recruitment/postings', <JobPostings />),
          guarded('/recruitment/candidates', <Candidates />),
          guarded('/recruitment/applications', <Applications />),
          guarded('/recruitment/applications/:applicationId', <ApplicationDetail />),
          guarded('/users', <Users />),
          guarded('/roles', <Roles />),
          guarded('/settings/company', <CompanySettings />),
          guarded('/status', <SystemStatus />),
        ],
      },
      {
        // The field-rep shell (ADR-007, F8): same auth, same API client, a different
        // layout and a much smaller route list. `AppShell` is never rendered here.
        element: <MobileShell />,
        children: [
          { path: '/m', element: <MobileHome /> },
          { path: '/m/device-check', element: <MobileCheckIn /> },
          { path: '/m/route', element: <MobileRoute /> },
          { path: '/m/leads/new', element: <MobileLeadCapture /> },
          { path: '/m/visits/:visitId', element: <MobileVisit /> },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
])

// Restore the session before the first render finishes, so a returning user does not see
// the login screen flash past on the way to the page they asked for.
void useAuth.getState().bootstrap()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <RouterProvider router={router} />
      </ErrorBoundary>
      <Toaster />
    </QueryClientProvider>
  </React.StrictMode>,
)
